import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import ReactApexChart from "react-apexcharts";
import { maskCNPJ } from "../../../utils/masks";
import "./CreditoPreditivo.css";

/* ================== Formatação ================== */
function numberBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
function moneyBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
function percentBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n) * 100;
  return `${x.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
}
function companyAge(isoOrBr) {
  if (!isoOrBr) return null;
  const m = isoOrBr.includes("/")
    ? moment(isoOrBr, "DD/MM/YYYY")
    : moment(isoOrBr);
  if (!m.isValid()) return null;
  const years = moment().diff(m, "years");
  return `${years} ano${years === 1 ? "" : "s"}`;
}

/* Mapa de cor para os sinais-chave → classe do tile */
function signalTone(value, type) {
  const v = (value || "").toUpperCase();
  const g = "green";
  const a = "amber";
  const r = "red";
  const maps = {
    gravidadeAtual: {
      BAIXA: g,
      MÉDIA: a,
      MEDIA: a,
      ALTA: r,
      CRÍTICA: r,
      CRITICA: r,
    },
    recenciaOcorrencias: {
      ANTIGA: g,
      INTERMEDIÁRIA: a,
      INTERMEDIARIA: a,
      RECENTE: r,
    },
    concentracaoRisco: { BAIXA: g, MÉDIA: a, MEDIA: a, ALTA: r },
    consistenciaOperacional: { ALTA: g, MÉDIA: a, MEDIA: a, BAIXA: r },
  };
  return (maps[type] && maps[type][v]) || "neutral";
}

/* ================== Gauge (SVG simples) ================== */
function Gauge({ score = 0 }) {
  const size = 200;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, (Number(score) / 1000) * 100));
  const offset = circ - (pct / 100) * circ;

  return (
    <div className="cp__gauge">
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle className="cp__gauge-track" cx={cx} cy={cx} r={r} />
        <circle
          className="cp__gauge-arc"
          cx={cx}
          cy={cx}
          r={r}
          strokeDasharray={circ}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="cp__gauge-center">
        <div className="cp__gauge-value">{Math.round(Number(score) || 0)}</div>
        <div className="cp__gauge-max">de 1000</div>
      </div>
    </div>
  );
}

function CardHead({ icon, title, sub }) {
  return (
    <div className="cp__card-head">
      <div className="cp__card-icon">{icon}</div>
      <div>
        <div className="cp__card-title">{title}</div>
        {sub && <div className="cp__card-sub">{sub}</div>}
      </div>
    </div>
  );
}

const TONE_LEVEL = { green: 1, amber: 2, red: 3, neutral: 0 };

function Signal({ label, value, type }) {
  const tone = signalTone(value, type);
  const level = TONE_LEVEL[tone] || 0;
  return (
    <div className="cp__signal">
      <div className="cp__signal-label">{label}</div>
      <div
        className="cp__meter"
        role="meter"
        aria-valuenow={level}
        aria-valuemax={3}
      >
        {[1, 2, 3].map((seg) => (
          <span
            key={seg}
            className={`cp__meter-seg${
              seg <= level ? ` cp__meter-seg--${tone}` : ""
            }`}
          />
        ))}
      </div>
      <div className={`cp__signal-value cp__signal-value--${tone}`}>
        {value || "—"}
      </div>
    </div>
  );
}

/* ================== Panorama temporal (ApexCharts) ================== */
function compactMoneyBR(n) {
  const x = Number(n) || 0;
  if (Math.abs(x) >= 1_000_000)
    return `R$ ${(x / 1_000_000).toLocaleString("pt-BR", {
      maximumFractionDigits: 1,
    })}M`;
  if (Math.abs(x) >= 1_000)
    return `R$ ${(x / 1_000).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    })}k`;
  return moneyBR(x);
}

function Kpi({ ico, tone, val, label }) {
  return (
    <div className={`cp__kpi cp__kpi--${tone}`}>
      <div className="cp__kpi-ico">{ico}</div>
      <div className="cp__kpi-body">
        <div className="cp__kpi-val">{val}</div>
        <div className="cp__kpi-label">{label}</div>
      </div>
    </div>
  );
}

function PanoramaChart({ eventos = [], risk }) {
  const wrapRef = useRef(null);
  const [accent, setAccent] = useState("#2563eb");

  useEffect(() => {
    if (!wrapRef.current) return;
    const v = getComputedStyle(wrapRef.current)
      .getPropertyValue("--cp-accent")
      .trim();
    if (v) setAccent(v);
  }, [risk]);

  const dados = useMemo(
    () =>
      [...eventos].sort((a, b) =>
        String(a.mes || "").localeCompare(String(b.mes || "")),
      ),
    [eventos],
  );

  const categories = dados.map((e) =>
    moment(e.mes, "YYYY-MM").isValid()
      ? moment(e.mes, "YYYY-MM").format("MM/YY")
      : e.mes,
  );
  const protestos = dados.map((e) => Number(e.protestos || 0));
  const pendencias = dados.map((e) => Number(e.pendencias || 0));
  const cheques = dados.map((e) => Number(e.chequesSemFundo || 0));
  const valores = dados.map((e) => Number(e.valorTotal || 0));

  const totProtestos = protestos.reduce((a, b) => a + b, 0);
  const totPendencias = pendencias.reduce((a, b) => a + b, 0);
  const totCheques = cheques.reduce((a, b) => a + b, 0);
  const totValor = valores.reduce((a, b) => a + b, 0);
  const semOcorrencias =
    totProtestos + totPendencias + totCheques === 0 && totValor === 0;

  const series = [
    { name: "Protestos", type: "column", data: protestos },
    { name: "Pendências", type: "column", data: pendencias },
    { name: "Cheques s/ fundo", type: "column", data: cheques },
    { name: "Valor total", type: "area", data: valores },
  ];

  const options = {
    chart: {
      type: "line",
      stacked: false,
      fontFamily: "inherit",
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true, easing: "easeinout", speed: 650 },
      dropShadow: {
        enabled: true,
        enabledOnSeries: [3],
        top: 6,
        left: 0,
        blur: 10,
        color: accent,
        opacity: 0.18,
      },
    },
    colors: ["#e11d48", "#f59e0b", "#8b5cf6", accent],
    plotOptions: {
      bar: {
        columnWidth: dados.length <= 3 ? "26%" : dados.length <= 6 ? "42%" : "60%",
        borderRadius: 5,
        borderRadiusApplication: "end",
      },
    },
    stroke: { width: [0, 0, 0, 3], curve: "smooth", lineCap: "round" },
    fill: {
      type: ["solid", "solid", "solid", "gradient"],
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.32,
        opacityTo: 0.02,
        stops: [0, 95],
      },
    },
    markers: {
      size: [0, 0, 0, 0],
      strokeWidth: 2,
      strokeColors: "#ffffff",
      hover: { size: 6 },
    },
    dataLabels: { enabled: false },
    legend: {
      position: "top",
      horizontalAlign: "left",
      fontSize: "13px",
      fontWeight: 600,
      labels: { colors: "#64748b" },
      markers: { width: 10, height: 10, radius: 6, offsetX: -2 },
      itemMargin: { horizontal: 9, vertical: 4 },
    },
    grid: {
      borderColor: "#e6ebf3",
      strokeDashArray: 5,
      xaxis: { lines: { show: false } },
      padding: { left: 6, right: 6, top: 0, bottom: 0 },
    },
    xaxis: {
      categories,
      labels: { style: { colors: "#94a3b8", fontSize: "12px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: [
      {
        seriesName: "Protestos",
        min: 0,
        forceNiceScale: true,
        labels: {
          style: { colors: "#94a3b8", fontSize: "12px" },
          formatter: (v) => `${Math.round(v)}`,
        },
        title: {
          text: "Ocorrências",
          style: { color: "#64748b", fontWeight: 600, fontSize: "12px" },
        },
      },
      { seriesName: "Protestos", show: false },
      { seriesName: "Protestos", show: false },
      {
        opposite: true,
        seriesName: "Valor total",
        min: 0,
        labels: {
          style: { colors: "#94a3b8", fontSize: "12px" },
          formatter: (v) => compactMoneyBR(v),
        },
        title: {
          text: "Valor (R$)",
          style: { color: "#64748b", fontWeight: 600, fontSize: "12px" },
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      theme: "light",
      style: { fontSize: "12px", fontFamily: "inherit" },
      y: {
        formatter: (val, { seriesIndex }) =>
          seriesIndex === 3 ? moneyBR(val) : `${Math.round(val)}`,
      },
    },
    responsive: [
      {
        breakpoint: 640,
        options: {
          plotOptions: { bar: { columnWidth: "68%" } },
          legend: { itemMargin: { horizontal: 6 } },
        },
      },
    ],
  };

  return (
    <div className="cp__panorama" ref={wrapRef}>
      <div className="cp__kpis">
        <Kpi
          ico="⚖️"
          tone="rose"
          val={numberBR(totProtestos)}
          label="Protestos"
        />
        <Kpi
          ico="📄"
          tone="amber"
          val={numberBR(totPendencias)}
          label="Pendências"
        />
        <Kpi
          ico="🧾"
          tone="violet"
          val={numberBR(totCheques)}
          label="Cheques s/ fundo"
        />
        <Kpi
          ico="💰"
          tone="accent"
          val={compactMoneyBR(totValor)}
          label="Valor acumulado"
        />
      </div>

      {semOcorrencias ? (
        <div className="cp__panorama-empty">
          <div className="cp__panorama-empty-ico">✅</div>
          <div className="cp__panorama-empty-title">
            Nenhuma ocorrência negativa no período
          </div>
          <div className="cp__panorama-empty-sub">
            Sem protestos, pendências ou cheques nos meses analisados. A
            estabilidade temporal é um sinal positivo para o score.
          </div>
        </div>
      ) : (
        <div className="cp__panorama-chart">
          <ReactApexChart
            options={options}
            series={series}
            type="line"
            height={330}
          />
        </div>
      )}
    </div>
  );
}

/* ================== Componente principal ================== */
export default function CreditoPreditivo({ data }) {
  const d = data || {};
  const dc = d.dadosCadastrais || {};
  const m = d.metricasDerivadas || {};
  const rec = d.recomendacaoDeCredito || {};
  const pano = d.panoramaTemporal || {};

  const risk = d.classificacaoRisco;
  const idade = companyAge(dc.dataFundacao);
  const eventos = pano.eventosPorMes || [];
  const drivers = d.driversDeRisco || [];
  const maxAbsImpacto = Math.max(
    1,
    ...drivers.map((x) => Math.abs(Number(x.impacto || 0))),
  );

  return (
    <div className="cp" data-risk={risk || undefined}>
      <section className="cp__card">
        <CardHead icon="📌" title="Resumo" sub={pano.janelaAnalisada} />
        <p className="cp__resumo">
          {d.resumoExecutivo || pano.observacao || (
            <span className="cp__empty">Sem resumo disponível.</span>
          )}
        </p>
      </section>
      {/* ── SCORE + RESUMO ── */}
      <div className="cp__row cp__row--score">
        <section className="cp__card cp__score">
          <CardHead icon="🎯" title="Score estimado" />
          <Gauge score={Number(d.scoreIA ?? 0)} />
          <div className="cp__prob">
            <div className="cp__prob-value">
              {numberBR(d.probabilidadeInadimplencia12M)}%
            </div>
            <div className="cp__prob-label">
              Prob. de inadimplência em 12 meses
            </div>
          </div>
        </section>

        <div className="cp__col">
          <section className="cp__card">
            <CardHead icon="📝" title="Recomendação de crédito" />
            <div className="cp__rec-key">
              <div className="cp__rec-decision">
                <div className="cp__rec-decision-label">Decisão</div>
                <div className="cp__rec-decision-value">
                  {rec.decisao || "—"}
                </div>
              </div>
              <div className="cp__rec-limit">
                <div className="cp__rec-limit-label">Limite sugerido</div>
                <div className="cp__rec-limit-value">
                  {moneyBR(rec.limiteSugerido?.valor)}
                </div>
              </div>
            </div>
            {(rec.condicoes || []).length > 0 && (
              <div className="cp__list">
                {rec.condicoes.map((c, i) => (
                  <div className="cp__list-item" key={i}>
                    {c}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* ── SINAIS + DRIVERS ── */}
      <div className="cp__row cp__row--2">
        <section className="cp__card">
          <CardHead icon="🧭" title="Sinais-chave" />
          <div className="cp__signals">
            <Signal
              label="Gravidade atual"
              value={d.sinaisChave?.gravidadeAtual}
              type="gravidadeAtual"
            />
            <Signal
              label="Recência"
              value={d.sinaisChave?.recenciaOcorrencias}
              type="recenciaOcorrencias"
            />
            <Signal
              label="Concentração"
              value={d.sinaisChave?.concentracaoRisco}
              type="concentracaoRisco"
            />
            <Signal
              label="Consistência"
              value={d.sinaisChave?.consistenciaOperacional}
              type="consistenciaOperacional"
            />
          </div>
        </section>

        <section className="cp__card">
          <CardHead icon="⚠️" title="Fatores que impactam o score" />
          <div className="cp__drivers">
            {drivers.length === 0 && (
              <span className="cp__empty">Nenhum fator informado.</span>
            )}
            {drivers.map((drv, i) => {
              const impacto = Number(drv.impacto || 0);
              const neg = impacto < 0;
              const w = Math.round((Math.abs(impacto) / maxAbsImpacto) * 100);
              return (
                <div className="cp__driver" key={i}>
                  <div className="cp__driver-head">
                    <span className="cp__driver-name">{drv.nome}</span>
                    <span
                      className={`cp__driver-impact cp__driver-impact--${
                        neg ? "neg" : "pos"
                      }`}
                    >
                      {impacto > 0 ? "+" : ""}
                      {impacto}
                    </span>
                  </div>
                  {drv.descricao && (
                    <div className="cp__driver-desc">{drv.descricao}</div>
                  )}
                  <div className="cp__driver-bar">
                    <div
                      className={`cp__driver-fill cp__driver-fill--${
                        neg ? "neg" : "pos"
                      }`}
                      style={{ width: `${w}%` }}
                    />
                  </div>
                  {drv.peso != null && (
                    <div className="cp__driver-weight">
                      Peso {Math.round((drv.peso || 0) * 100)}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* ── PANORAMA TEMPORAL ── */}
      {eventos.length > 0 && (
        <section className="cp__card">
          <CardHead
            icon="📈"
            title="Panorama temporal"
            sub={pano.janelaAnalisada}
          />
          {pano.observacao && <p className="cp__resumo">{pano.observacao}</p>}
          <PanoramaChart eventos={eventos} risk={risk} />
        </section>
      )}
    </div>
  );
}
